import { useEffect, useState } from 'react';
import { Loader2, Boxes } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CUT_OPTIONS = [
  { id: 'full', value: 1.0, label: 'Pleine hauteur', hint: 'Aucune coupe : tous les murs à la HSP — volume fermé, intérieur peu visible' },
  { id: 'half', value: 0.5, label: 'Mi-hauteur', hint: 'Plan de coupe à 50 % de la HSP sur TOUS les murs — maquette en coupe classique, recommandé' },
  { id: 'low', value: 0.3, label: 'Basse', hint: 'Plan de coupe à 30 % — murs bas uniformes, intérieur entièrement lisible' },
];

export const DOOR_STYLES = [
  { id: 'closed', label: 'Fermées', hint: 'Vantail dans le plan du mur' },
  { id: 'open45', label: 'Ouvertes 45°', hint: 'Vantail entrouvert' },
  { id: 'open90', label: 'Ouvertes 90°', hint: 'Vantail perpendiculaire' },
];
export const DEFAULT_DOOR_STYLE = 'closed';
export const ASPECTS = [
  { id: '1:1', label: 'Carré 1:1', hint: '1024 × 1024 — aperçus / galerie' },
  { id: '16:9', label: 'Paysage 16:9', hint: '1920 × 1080 — livrable client, fond blanc (détourage)' },
];
const storageKey = (projectId) => `pe_whitebox_cut_${projectId}`;
export const loadCutPreference = (projectId) => {
  try { const v = parseFloat(localStorage.getItem(storageKey(projectId))); return Number.isFinite(v) ? v : 0.5; } catch { return 0.5; }
};

/** V15.02 — Choix de la coupe des façades + aperçu live avant génération/ancrage. V15.19 — format 1:1 / 16:9, fond blanc. */
export const WhiteboxCutDialog = ({ open, onClose, projectId, lotId, vueAngle, busy, onConfirm }) => {
  const [cut, setCut] = useState(() => loadCutPreference(projectId));
  const [tints, setTints] = useState(() => { try { return localStorage.getItem(`pe_whitebox_tints_${projectId}`) !== '0'; } catch { return true; } });
  const [doorStyle, setDoorStyle] = useState(() => { try { return localStorage.getItem(`pe_whitebox_doors_${projectId}`) || DEFAULT_DOOR_STYLE; } catch { return DEFAULT_DOOR_STYLE; } });
  const [backFull, setBackFull] = useState(() => { try { return localStorage.getItem(`pe_whitebox_backfull_${projectId}`) === '1'; } catch { return false; } });
  const [aspect, setAspect] = useState(() => { try { return localStorage.getItem(`pe_whitebox_aspect_${projectId}`) === '16:9' ? '16:9' : '1:1'; } catch { return '1:1'; } });
  const [preview, setPreview] = useState({ src: null, loading: false, error: null });

  useEffect(() => {
    if (!open || !lotId) return undefined;
    let alive = true;
    let obj = null;
    setPreview((p) => ({ ...p, loading: true, error: null }));
    const url = `${API}/projects/${projectId}/lots/${lotId}/whitebox-deterministic/preview?peripheral_cut=${cut}&floor_tints=${tints}&door_style=${doorStyle}&back_walls_full=${backFull}&aspect=${encodeURIComponent(aspect)}&vue_angle=${encodeURIComponent(vueAngle || 'Aérienne isométrique 45°')}`;
    fetch(url, { credentials: 'include' })
      .then(async (r) => (r.ok ? r.blob() : Promise.reject(new Error((await r.json().catch(() => ({})))?.detail || `HTTP ${r.status}`))))
      .then((b) => { if (alive) { obj = URL.createObjectURL(b); setPreview({ src: obj, loading: false, error: null }); } })
      .catch((e) => { if (alive) setPreview({ src: null, loading: false, error: e.message }); });
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [open, projectId, lotId, cut, tints, doorStyle, backFull, aspect, vueAngle]);
  const [legend, setLegend] = useState([]);
  useEffect(() => {
    if (!open || !lotId) return undefined;
    let alive = true;
    fetch(`${API}/projects/${projectId}/lots/${lotId}/whitebox-deterministic/floor-tints`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { legend: [] }))
      .then((d) => { if (alive) setLegend(d.legend || []); })
      .catch(() => { if (alive) setLegend([]); });
    return () => { alive = false; };
  }, [open, projectId, lotId]);

  const confirm = () => {
    try { localStorage.setItem(storageKey(projectId), String(cut)); localStorage.setItem(`pe_whitebox_tints_${projectId}`, tints ? '1' : '0'); localStorage.setItem(`pe_whitebox_doors_${projectId}`, doorStyle); localStorage.setItem(`pe_whitebox_backfull_${projectId}`, backFull ? '1' : '0'); localStorage.setItem(`pe_whitebox_aspect_${projectId}`, aspect); } catch { /* no-op */ }
    onConfirm?.(cut, tints, doorStyle, backFull, aspect);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-4xl max-h-[94vh] flex flex-col gap-3 overflow-hidden" style={{ background: 'var(--c-surface-0)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }} data-testid="whitebox-cut-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px]"><Boxes className="w-4 h-4" /> Maquette blanche — plan de coupe</DialogTitle>
          <DialogDescription className="text-[11px] leading-snug" style={{ color: 'var(--c-text-2)' }}>
            Hauteur du plan de coupe (tous les murs, cloisons basses conservées) · portes = vantail + seuil, fenêtres = vitrage, extérieurs = sol gris pâle · fond blanc (détourage). L'aperçu est le rendu exact (sans IA) ancré comme référence de l'étape 1.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-[240px_1fr] gap-4 min-h-0 flex-1">
          <div className="flex flex-col gap-2 overflow-y-auto pr-1 min-h-0" style={{ maxHeight: 'calc(94vh - 190px)' }} role="radiogroup" data-testid="whitebox-cut-options">
            <div className="text-[11px] px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--c-border)' }} data-testid="whitebox-aspect">
              <div className="font-semibold mb-1">Format de l'image</div>
              <div className="flex gap-1">
                {ASPECTS.map((a) => (
                  <button key={a.id} type="button" onClick={() => setAspect(a.id)} title={a.hint}
                          className="flex-1 text-[10.5px] px-1.5 py-1 rounded"
                          style={{ background: aspect === a.id ? 'rgba(232,241,248,0.18)' : 'transparent', border: `1px solid ${aspect === a.id ? '#C9D9E8' : 'var(--c-border)'}` }}
                          data-testid={`whitebox-aspect-${a.id === '16:9' ? '16-9' : '1-1'}`}>
                    {a.label}
                  </button>
                ))}
              </div>
              <div className="text-[10.5px] mt-1" style={{ color: 'var(--c-text-2)' }}>{ASPECTS.find((a) => a.id === aspect)?.hint}</div>
            </div>
            {CUT_OPTIONS.map((o) => {
              const active = Math.abs(o.value - cut) < 1e-6;
              return (
                <button
                  key={o.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setCut(o.value)}
                  className="text-left rounded-lg px-3 py-2 transition-colors"
                  style={{ background: active ? 'rgba(232,241,248,0.14)' : 'rgba(255,255,255,0.03)', border: `1px solid ${active ? '#C9D9E8' : 'var(--c-border)'}` }}
                  data-testid={`whitebox-cut-${o.id}`}
                >
                  <div className="text-[12px] font-semibold">{o.label} <span className="text-[10px] font-normal opacity-60">· {Math.round(o.value * 100)} %</span></div>
                  <div className="text-[10.5px]" style={{ color: 'var(--c-text-2)' }}>{o.hint}</div>
                </button>
              );
            })}
            <div className="text-[11px] px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--c-border)' }} data-testid="whitebox-door-style">
              <div className="font-semibold mb-1">Portes intérieures</div>
              <div className="flex gap-1">
                {DOOR_STYLES.map((d) => (
                  <button key={d.id} type="button" onClick={() => setDoorStyle(d.id)} title={d.hint}
                          className="flex-1 text-[10.5px] px-1.5 py-1 rounded"
                          style={{ background: doorStyle === d.id ? 'rgba(232,241,248,0.18)' : 'transparent', border: `1px solid ${doorStyle === d.id ? '#C9D9E8' : 'var(--c-border)'}` }}
                          data-testid={`whitebox-door-${d.id}`}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-start gap-2 text-[11px] px-3 py-2 rounded-lg cursor-pointer" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--c-border)' }} data-testid="whitebox-tints-toggle">
              <input type="checkbox" className="mt-0.5" checked={tints} onChange={(e) => setTints(e.target.checked)} data-testid="whitebox-tints-input" />
              <span className="min-w-0">
                <strong>Sols teintés par matériau</strong> <span className="opacity-60">· recommandé</span>
                <div className="text-[10.5px]" style={{ color: 'var(--c-text-2)' }}>Sol résolu dans la Matériauthèque → teinte pâlie du matériau ; sinon beige = cuisine, bleu = pièces d'eau, gris = extérieur, blanc = sec. L'IA reçoit la légende teinte → matériau.</div>
                {tints && legend.length > 0 && (
                  <div className="mt-1.5 flex flex-col gap-0.5" data-testid="whitebox-tint-legend">
                    {legend.map((e) => (
                      <div key={e.room} className="flex items-center gap-1.5 text-[10px] text-gray-200 min-w-0" title={`${e.room} → ${e.material} (${e.hex})`} data-testid={`whitebox-tint-${e.room}`}>
                        <span className="w-2.5 h-2.5 rounded-sm border border-black/20 shrink-0" style={{ background: e.tint_hex }} /><span className="shrink-0">{e.room}</span><span className="opacity-60 truncate">· {e.material}</span>
                      </div>
                    ))}
                  </div>
                )}
                {tints && legend.length === 0 && <div className="mt-1 text-[10px] opacity-60" data-testid="whitebox-tint-legend-empty">Aucun sol résolu par pièce dans la Matériauthèque : teintes par famille.</div>}
              </span>
            </label>
            <label className="flex items-start gap-2 text-[11px] px-3 py-2 rounded-lg cursor-pointer" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--c-border)', opacity: cut >= 1 ? 0.5 : 1 }} data-testid="whitebox-backfull-toggle">
              <input type="checkbox" className="mt-0.5" checked={backFull} disabled={cut >= 1} onChange={(e) => setBackFull(e.target.checked)} data-testid="whitebox-backfull-input" />
              <span>
                <strong>Façades du fond toute hauteur</strong>
                <div className="text-[10.5px]" style={{ color: 'var(--c-text-2)' }}>Les murs extérieurs qui tournent le dos à la caméra ne cachent rien : ils gardent la HSP complète (baies incluses), seules les façades avant sont coupées.</div>
              </span>
            </label>
          </div>
          <div className="flex flex-col gap-2 min-h-0">
            <div className={`relative ${aspect === '16:9' ? 'aspect-video' : 'aspect-square'} w-full max-h-[calc(94vh-250px)] rounded-lg overflow-hidden flex items-center justify-center self-center`} style={{ background: '#ffffff', border: '1px solid var(--c-border)' }} data-testid="whitebox-cut-preview">
              {preview.src && <img src={preview.src} alt="Aperçu maquette blanche" className="w-full h-full object-contain" style={{ opacity: preview.loading ? 0.5 : 1 }} data-testid="whitebox-cut-preview-img" />}
              {preview.loading && <Loader2 className="absolute w-6 h-6 animate-spin" style={{ color: '#0B1220' }} />}
              {preview.error && <span className="text-[11px] px-4 text-center" style={{ color: '#B91C1C' }} data-testid="whitebox-cut-preview-error">{preview.error}</span>}
            </div>
            <button
              type="button"
              onClick={confirm}
              disabled={busy || preview.loading || !!preview.error}
              className="inline-flex items-center justify-center gap-1.5 text-[12px] px-3 py-2.5 rounded-lg font-semibold disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg, #E8F1F8 0%, #C9D9E8 100%)', color: '#0B1220' }}
              data-testid="whitebox-cut-confirm"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Boxes className="w-3.5 h-3.5" />} Générer et ancrer
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WhiteboxCutDialog;
